import React, { useMemo, useState, type ReactNode } from 'react';
import groupBy from 'lodash/groupBy';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { SourceType } from '../../../../common/constants/source-items';
import type { ImporterSourceItem } from '../../../../common/types';
import messages from './messages';

type Props = {
	sourceItems: ImporterSourceItem[];
	renderItem: (item: ImporterSourceItem) => React.ReactNode;
};

export const DEFAULT_SOURCE_ITEM_NUMBER = 20;

export const SourceGroup = ({ sourceItems, renderItem }: Props) => {
	const { formatMessage } = useIntl();

	const [showMore, setShowMore] = useState(false);

	const groups = useMemo(() => {
		const groupedSourceItems = groupBy(sourceItems, 'sourceType');
		return [
			{
				key: SourceType.THIRD_PARTY,
				header: messages.thirdPartySourceHeader,
				description: messages.thirdPartySourceDescription,
				data: groupedSourceItems[SourceType.THIRD_PARTY],
			},
			{
				key: SourceType.CSV,
				header: messages.csvSectionHeader,
				description: messages.csvSectionDescription,
				data: groupedSourceItems[SourceType.CSV],
			},
		];
	}, [sourceItems]);

	return groups.map(
		(group) =>
			group.data && (
				<Box key={group.key} xcss={sourceGroupStyles}>
					<Box xcss={headerStyles} as="h4">
						{formatMessage(group.header)}
					</Box>
					<Box xcss={descriptionStyles}>
						{formatMessage(group.description, {
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							strong: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
						})}
					</Box>
					{group.data.length <= DEFAULT_SOURCE_ITEM_NUMBER || showMore ? (
						<Box xcss={buttonGroupStyles} role="group">
							{group.data.map((item) => renderItem(item))}
						</Box>
					) : (
						<>
							<Box xcss={buttonGroupStyles} role="group">
								{group.data.slice(0, DEFAULT_SOURCE_ITEM_NUMBER).map((item) => renderItem(item))}
							</Box>
							<Box paddingBlockStart="space.200">
								<Button
									iconBefore={<ChevronDownIcon label="chevronDownIcon" />}
									appearance="link"
									onClick={() => setShowMore(true)}
								>
									{formatMessage(messages.showMore)}
								</Button>
							</Box>
						</>
					)}
				</Box>
			),
	);
};

const sourceGroupStyles = xcss({
	padding: 'space.250',
	backgroundColor: 'elevation.surface.sunken',
	borderRadius: 'border.radius.200',
	marginBottom: 'space.200',
});

const headerStyles = xcss({
	font: token('font.heading.xsmall'),
});

const descriptionStyles = xcss({
	font: token('font.body'),
	color: 'color.text.subtle',
});

const buttonGroupStyles = xcss({
	display: 'flex',
	flexFlow: 'row wrap',
	gap: 'space.200',
	marginTop: 'space.200',
});
